/**
 * workoutValidation — client-side input validation for workout logging.
 *
 * These are sanity checks to prevent obviously bad data.
 * Server-side enforcement is the true authority (future migration).
 *
 * Constraints:
 * - Weight: 0–2000 lbs (0–907 kg) — 0 allowed for bodyweight exercises
 * - Reps: 1–100 (or duration in seconds for timed exercises)
 * - Sets: 1–50
 * - No NaN, no negative
 */

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const WEIGHT_MIN_LB = 0;
const WEIGHT_MAX_LB = 2000;
const WEIGHT_MIN_KG = 0;
const WEIGHT_MAX_KG = 907;
const REPS_MIN = 1;
const REPS_MAX = 100;
const SETS_MIN = 1;
const SETS_MAX = 50;

export function validateWeight(value: string | number, unit: 'lb' | 'kg' = 'lb'): ValidationResult {
  const errors: string[] = [];
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (!isFinite(num) || isNaN(num)) {
    errors.push('Weight must be a valid number.');
    return { valid: false, errors };
  }

  if (num < 0) {
    errors.push('Weight cannot be negative.');
  } else if (unit === 'lb') {
    if (num > WEIGHT_MAX_LB) errors.push(`Weight cannot exceed ${WEIGHT_MAX_LB} lbs.`);
  } else {
    if (num > WEIGHT_MAX_KG) errors.push(`Weight cannot exceed ${WEIGHT_MAX_KG} kg.`);
  }

  return { valid: errors.length === 0, errors };
}

export function validateReps(value: string | number): ValidationResult {
  const errors: string[] = [];
  const num = typeof value === 'string' ? parseInt(value, 10) : value;

  if (!isFinite(num) || isNaN(num)) {
    errors.push('Reps must be a valid number.');
    return { valid: false, errors };
  }

  if (num < REPS_MIN) errors.push(`Reps must be at least ${REPS_MIN}.`);
  if (num > REPS_MAX) errors.push(`Reps cannot exceed ${REPS_MAX}.`);
  if (num !== Math.floor(num)) errors.push('Reps must be a whole number.');

  return { valid: errors.length === 0, errors };
}

export function validateSets(value: string | number): ValidationResult {
  const errors: string[] = [];
  const num = typeof value === 'string' ? parseInt(value, 10) : value;

  if (!isFinite(num) || isNaN(num)) {
    errors.push('Sets must be a valid number.');
    return { valid: false, errors };
  }

  if (num < SETS_MIN) errors.push(`Sets must be at least ${SETS_MIN}.`);
  if (num > SETS_MAX) errors.push(`Sets cannot exceed ${SETS_MAX}.`);
  if (num !== Math.floor(num)) errors.push('Sets must be a whole number.');

  return { valid: errors.length === 0, errors };
}

/**
 * Volume sanity ceiling — prevents impossible combos like 1000 lb × 50 reps × 20 sets.
 * Threshold: 100,000 lb total volume per entry (≈ 45,360 kg).
 * This is far beyond any realistic single-exercise session.
 */
const MAX_VOLUME_LB = 100_000;

/**
 * Validate a full exercise entry (weight + reps + sets).
 * Returns all errors concatenated.
 */
export function validateExerciseEntry(
  weight: string,
  reps: string,
  sets: string,
  weightUnit: 'lb' | 'kg' = 'lb',
): ValidationResult {
  const allErrors: string[] = [];

  const w = validateWeight(weight, weightUnit);
  const r = validateReps(reps);
  const s = validateSets(sets);

  allErrors.push(...w.errors, ...r.errors, ...s.errors);

  // Volume sanity check (only if individual fields are valid numbers)
  if (w.valid && r.valid && s.valid) {
    const wNum = parseFloat(weight);
    const rNum = parseInt(reps, 10);
    const sNum = parseInt(sets, 10);
    const volumeLb = weightUnit === 'kg' ? wNum * 2.205 * rNum * sNum : wNum * rNum * sNum;
    if (volumeLb > MAX_VOLUME_LB) {
      allErrors.push('Total volume exceeds plausible limits. Check your entries.');
    }

    // Weight × reps plausibility
    const wrp = validateWeightRepPlausibility(weight, reps, weightUnit);
    allErrors.push(...wrp.errors);
  }

  return { valid: allErrors.length === 0, errors: allErrors };
}

/**
 * Weight × reps plausibility matrix.
 *
 * Extremely heavy loads paired with high reps are physiologically implausible.
 * These thresholds are deliberately generous; server-side enforcement is the
 * true authority.
 *
 * Rule: if weight ≥ threshold (in lb), max reps is capped.
 */
const WEIGHT_REP_THRESHOLDS_LB: { minWeight: number; maxReps: number }[] = [
  { minWeight: 800, maxReps: 5 },
  { minWeight: 600, maxReps: 12 },
  { minWeight: 400, maxReps: 30 },
];

export function validateWeightRepPlausibility(
  weight: string | number,
  reps: string | number,
  weightUnit: 'lb' | 'kg' = 'lb',
): ValidationResult {
  const errors: string[] = [];
  const w = typeof weight === 'string' ? parseFloat(weight) : weight;
  const r = typeof reps === 'string' ? parseInt(String(reps), 10) : reps;

  if (!isFinite(w) || !isFinite(r)) return { valid: true, errors: [] };

  const weightLb = weightUnit === 'kg' ? w * 2.205 : w;

  for (const { minWeight, maxReps } of WEIGHT_REP_THRESHOLDS_LB) {
    if (weightLb >= minWeight && r > maxReps) {
      errors.push(
        `${Math.round(weightLb)} lb × ${r} reps exceeds plausible limits. Verify your entry.`,
      );
      break;
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Session-level entry cap.
 *
 * Prevents unreasonable numbers of exercises in a single session.
 * Generous limit — real workouts rarely exceed 15 exercises.
 */
const MAX_EXERCISES_PER_SESSION = 25;

export function validateSessionSize(entryCount: number): ValidationResult {
  const errors: string[] = [];
  if (entryCount > MAX_EXERCISES_PER_SESSION) {
    errors.push(`A single session cannot exceed ${MAX_EXERCISES_PER_SESSION} exercises.`);
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Workout date validation.
 *
 * Rejects future dates and dates more than 1 year in the past.
 * Allows a small margin (1 hour) for timezone drift.
 */
const MAX_PAST_DAYS = 365;
const FUTURE_MARGIN_MS = 60 * 60 * 1000; // 1 hour

export function validateWorkoutDate(date: Date): ValidationResult {
  const errors: string[] = [];
  const now = Date.now();

  if (date.getTime() > now + FUTURE_MARGIN_MS) {
    errors.push('Workout date cannot be in the future.');
  }

  const pastLimit = now - MAX_PAST_DAYS * 24 * 60 * 60 * 1000;
  if (date.getTime() < pastLimit) {
    errors.push('Workout date cannot be more than 1 year in the past.');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Duplicate submission guard.
 *
 * Tracks recent submissions by lift key + timestamp. Returns true if this
 * looks like a duplicate (same lift submitted within the cooldown window).
 * Caller should prevent the save and show a warning.
 */
const recentSubmissions = new Map<string, number>();
const DUPLICATE_COOLDOWN_MS = 5_000;

/**
 * Check if a lift key was recently submitted (within cooldown window).
 * Does NOT record the submission — call `recordSubmission` after a successful save.
 */
export function isDuplicateSubmission(liftKey: string): boolean {
  const now = Date.now();
  const lastSubmit = recentSubmissions.get(liftKey);
  return !!(lastSubmit && now - lastSubmit < DUPLICATE_COOLDOWN_MS);
}

/**
 * Record that these lift keys were just submitted, so future duplicate
 * checks within the cooldown window will fire. Call this AFTER a
 * successful save, not before.
 */
export function recordSubmissions(liftKeys: string[]): void {
  const now = Date.now();
  for (const key of liftKeys) {
    recentSubmissions.set(key, now);
  }
  // Prune old entries
  if (recentSubmissions.size > 50) {
    for (const [key, ts] of recentSubmissions) {
      if (now - ts > DUPLICATE_COOLDOWN_MS * 2) recentSubmissions.delete(key);
    }
  }
}
